import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EXCEPTION_TYPES } from '@dispatchdash/shared';
import {
  tripsApi,
  scanningApi,
  exceptionsApi,
  shouldQueueOffline,
  type CargoCheck,
  type ScanCount,
  type ScanEvent,
  type Trip,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { CrewLayout } from '@/components/Layout';
import { Nav } from '@/components/Nav';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { OfflineIndicator, useOfflineSync } from '@/offline';
import { compressImage, EVIDENCE_COMPRESS_OPTIONS } from '@/lib/imageCompression';
import {
  queueEvent,
  cacheManifestBarcodes,
  isOnCachedManifest,
  cacheCrewTrip,
  getCachedCrewTrip,
  getQueuedEvents,
  OFFLINE_QUEUE_CHANGED,
  type QueuedEvent,
} from '@/offline/queue';
import { newCrewActionMeta, type CrewActionMeta } from '@/lib/offlineReplay';
import { crewNavItems } from '@/pages/navItems';
import {
  classifyCameraError,
  startZxingContinuousScan,
  type ContinuousScanHandle,
  type VideoDevice,
} from '@/lib/barcodeCamera';
import {
  mapScanFeedback,
  playScanFeedbackTone,
  vibrateScanFeedback,
  type ScanFeedback,
} from '@/lib/scanFeedback';
import {
  aggregateProgress,
  scanIsReconciled,
  scanNeedsAttention,
} from '@/lib/scanReconcile';
import { resolveCrewNextTask } from '@/lib/crewNextTask';
import { applyQueuedCrewActions, eventTypeFromNextTask } from '@/lib/crewStopAdvance';
import { CARGO_COPY } from '@/lib/crewCargoCheck';
import { CrewActionBar, CrewIcon, CrewMessage } from '@/components/CrewUi';
import { CrewCargoCheckPanel } from './CrewCargoCheckPanel';
import { useCrewCargoCheck } from './useCrewCargoCheck';
import { humanize, stopTypeLabel } from '@/lib/format';
import { useToast } from '@/components/Feedback';

/** Quick-pick reasons for the exception form (fills type + reason in one tap). */
const EXCEPTION_QUICK_PICKS: { label: string; type: (stopType?: string) => string }[] = [
  { label: 'Damaged', type: () => 'damage' },
  { label: 'Missing', type: (stopType) => (stopType === 'pickup' ? 'short_count_pickup' : 'short_count_drop') },
  { label: 'Refused', type: () => 'consignee_refuses' },
  { label: 'Extra item', type: () => 'over_count' },
];

function getPosition(): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  });
}

const FEEDBACK_MS = 1400;

/** Scan tab copy for stops without a manifest (CREW_API_CONTRACT §14.5). */
const CARGO_SCAN_COPY = {
  noManifest: 'No manifest for this stop — confirm the pieces instead.',
  arriveFirst: 'Arrive and start servicing this stop on the Trip tab first. Then confirm the pieces here.',
} as const;

/** "Cargo confirmed · 12 pcs · PIC Maria Santos" */
function cargoSummaryText(check: CargoCheck): string {
  return `${CARGO_COPY.confirmedSummary}${check.countedPieces != null ? ` · ${check.countedPieces} pcs` : ''}${
    check.picName ? ` · PIC ${check.picName}` : ''
  }`;
}

type OverlayKind = 'feedback' | 'wrong_stop' | 'over_resolve';

export function CrewScan() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const queryStopId = searchParams.get('stopId') ?? '';
  const guided = searchParams.get('guided') === '1';

  const { data: trip, reload: reloadTrip } = useAsync(async () => {
    try {
      const current = await tripsApi.myCurrent();
      await cacheCrewTrip(current as unknown as Record<string, unknown> | null);
      return current;
    } catch (err) {
      const cached = await getCachedCrewTrip<Trip>();
      if (cached) return cached;
      throw err;
    }
  }, []);
  const { pendingCount: offlinePending, syncing, online } = useOfflineSync();

  const [selectedStopId, setSelectedStopId] = useState(queryStopId);
  const [barcode, setBarcode] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [decoderReady, setDecoderReady] = useState(false);
  const [counts, setCounts] = useState<ScanCount[]>([]);
  const [pendingScans, setPendingScans] = useState<ScanEvent[]>([]);
  const [overs, setOvers] = useState<ScanEvent[]>([]);
  const [videoDevices, setVideoDevices] = useState<VideoDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [feedback, setFeedback] = useState<ScanFeedback | null>(null);
  const [lastBarcode, setLastBarcode] = useState('');
  const [overlayKind, setOverlayKind] = useState<OverlayKind | null>(null);
  const [resolveScan, setResolveScan] = useState<ScanEvent | null>(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [scanPaused, setScanPaused] = useState(false);
  const [autoStarted, setAutoStarted] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);

  const queuedMessagePending = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scanHandleRef = useRef<ContinuousScanHandle | null>(null);
  const recordScanRef = useRef<(code: string, qty: number, entryMethod: string) => Promise<void>>(
    async () => undefined,
  );
  const inFlightRef = useRef(false);
  const scanPausedRef = useRef(false);
  const feedbackTimerRef = useRef<number | null>(null);

  const [showException, setShowException] = useState(false);
  const [exceptionType, setExceptionType] = useState<string>(EXCEPTION_TYPES[0]);
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionPhoto, setExceptionPhoto] = useState('');
  const [exceptionPhotoLoading, setExceptionPhotoLoading] = useState(false);
  const [exceptionLoading, setExceptionLoading] = useState(false);
  const [exceptionMessage, setExceptionMessage] = useState('');

  useEffect(() => {
    if (queryStopId) setSelectedStopId(queryStopId);
  }, [queryStopId]);

  // Offline-saved actions (e.g. a cargo check) shown on top of the last known trip.
  const [queuedActions, setQueuedActions] = useState<QueuedEvent[]>([]);
  const refreshQueued = useCallback(async () => {
    try {
      setQueuedActions(await getQueuedEvents());
    } catch {
      /* keep the last list */
    }
  }, []);
  const queuedPendingRef = useRef<number | null>(null);
  useEffect(() => {
    let chain: Promise<void> = Promise.resolve();
    const handle = async () => {
      let events: QueuedEvent[];
      try {
        events = await getQueuedEvents();
      } catch {
        return;
      }
      const pending = events.filter((e) => e.status !== 'failed').length;
      const prev = queuedPendingRef.current;
      queuedPendingRef.current = pending;
      // Sent: load the server's trip before dropping the overlay (no flicker back).
      if (prev !== null && pending < prev && navigator.onLine) await reloadTrip();
      setQueuedActions(events);
    };
    const onChange = () => {
      chain = chain.then(handle, handle);
    };
    onChange();
    window.addEventListener(OFFLINE_QUEUE_CHANGED, onChange);
    return () => window.removeEventListener(OFFLINE_QUEUE_CHANGED, onChange);
  }, [reloadTrip]);
  const overlayTrip = useMemo(
    () => (trip ? applyQueuedCrewActions(trip, queuedActions) : trip),
    [trip, queuedActions],
  );
  const [cargoChanging, setCargoChanging] = useState(false);
  const cargo = useCrewCargoCheck({
    tripId: trip?.id,
    onSaved: async () => {
      setCargoChanging(false);
      await reloadTrip();
    },
    onQueued: async () => {
      setCargoChanging(false);
      await refreshQueued();
    },
    onRejected: reloadTrip,
  });

  const stops = (trip?.stops ?? []).filter((s) => s.status !== 'departed');
  const activeStopId =
    selectedStopId ||
    stops.find((s) => s.status === 'servicing')?.id ||
    stops[0]?.id ||
    '';
  const activeStop = stops.find((s) => s.id === activeStopId);
  const hasException = Boolean(activeStop?.hasException);
  // A stop without manifest lines (cargoCheck present) confirms pieces instead of scanning.
  const cargoStop = overlayTrip?.stops?.find((s) => s.id === activeStopId);
  const cargoCheck = cargoStop?.cargoCheck ?? null;
  const cargoMode = Boolean(cargoCheck);
  const cargoStopStatus = cargoStop?.status ?? '';
  const pendingConfirmCount = pendingScans.length;
  const progress = aggregateProgress(counts);
  const needsAttention = scanNeedsAttention(counts, pendingConfirmCount);
  const reconciled = scanIsReconciled(counts, pendingConfirmCount, hasException);
  const workComplete = reconciled || hasException;
  const cameraMode = cameraActive && !workComplete && !cargoMode;

  const showFeedback = useCallback((fb: ScanFeedback, kind: OverlayKind = 'feedback') => {
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    setFeedback(fb);
    setOverlayKind(kind);
    // Sound + vibration always (gloves, noise, sunlight); reduced-motion only
    // affects the visual flash animation (handled in CSS).
    playScanFeedbackTone(fb.tone);
    vibrateScanFeedback(fb.tone);
    if (kind === 'feedback') {
      feedbackTimerRef.current = window.setTimeout(() => {
        setFeedback(null);
        setOverlayKind(null);
      }, FEEDBACK_MS);
    }
  }, []);

  const loadCounts = useCallback(async (stopId: string) => {
    if (!stopId) {
      setCounts([]);
      setPendingScans([]);
      setOvers([]);
      return;
    }
    try {
      const [result, pending, overList] = await Promise.all([
        scanningApi.counts(stopId),
        scanningApi.pending(stopId).catch(() => [] as ScanEvent[]),
        scanningApi.listOvers(stopId).catch(() => [] as ScanEvent[]),
      ]);
      setCounts(result);
      setPendingScans(pending);
      setOvers(overList);
      await cacheManifestBarcodes(
        stopId,
        result.map((c) => c.barcode),
      );
    } catch {
      /* Manifest may not be imported yet */
    }
  }, []);

  useEffect(() => {
    void loadCounts(activeStopId);
  }, [activeStopId, loadCounts]);

  useEffect(() => {
    if (!queuedMessagePending.current) return;
    if (online && !syncing && offlinePending === 0) {
      queuedMessagePending.current = false;
      setMessage('Saved scans sent');
      void loadCounts(activeStopId);
      const timer = window.setTimeout(() => setMessage(''), 4000);
      return () => window.clearTimeout(timer);
    }
  }, [online, syncing, offlinePending, activeStopId, loadCounts]);

  const stopCamera = useCallback(() => {
    scanHandleRef.current?.stop();
    scanHandleRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
    setDecoderReady(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const pauseForResolve = useCallback(
    (scan: ScanEvent, kind: 'over_resolve' | 'wrong_stop') => {
      scanPausedRef.current = true;
      setScanPaused(true);
      setResolveScan(scan);
      const fb = mapScanFeedback({
        kind: 'scan_result',
        result: scan.result,
        barcode: scan.barcode,
      });
      showFeedback(fb, kind);
    },
    [showFeedback],
  );

  const resumeScanning = useCallback(() => {
    scanPausedRef.current = false;
    setScanPaused(false);
    setResolveScan(null);
    setOverlayKind(null);
    setFeedback(null);
  }, []);

  const recordScan = useCallback(
    async (code: string, qty: number, entryMethod: string) => {
      if (!activeStopId || !code.trim()) return;
      if (inFlightRef.current || scanPausedRef.current) return;
      inFlightRef.current = true;
      setLoading(true);
      setError('');
      setMessage('');
      setLastBarcode(code.trim());
      let queued = 0;
      let succeeded = 0;
      let queuedOffManifest = false;
      let lastResult: ScanEvent['result'] | undefined;
      let lastEvent: ScanEvent | undefined;
      let shouldPause = false;

      for (let i = 0; i < qty; i++) {
        if (shouldPause) break;
        // One key per physical piece, shared by the live attempt and any offline replay.
        const action = newCrewActionMeta();
        try {
          const result = await scanningApi.scan({
            tripStopId: activeStopId,
            barcode: code.trim(),
            entryMethod,
            offlineCaptured: false,
            clientEventId: action.clientEventId,
            scannedAt: action.deviceTimestamp,
          });
          succeeded++;
          lastResult = result.scan.result;
          lastEvent = result.scan;
          setCounts(result.counts);

          if (result.scan.result === 'over') {
            shouldPause = true;
            pauseForResolve(result.scan, 'over_resolve');
            break;
          }
          if (result.scan.result === 'wrong_stop') {
            shouldPause = true;
            pauseForResolve(result.scan, 'wrong_stop');
            break;
          }
          if (result.scan.result === 'not_on_manifest') {
            showFeedback(
              mapScanFeedback({
                kind: 'scan_result',
                result: 'not_on_manifest',
                barcode: code.trim(),
              }),
            );
          } else {
            showFeedback(
              mapScanFeedback({
                kind: 'scan_result',
                result: result.scan.result,
                barcode: code.trim(),
              }),
            );
          }
        } catch (err) {
          if (shouldQueueOffline(err)) {
            try {
              await queueEvent({
                type: 'scan',
                payload: { stopId: activeStopId, barcode: code.trim(), entryMethod },
                ...action,
              });
              queued++;
              queuedMessagePending.current = true;
              setCounts((prev) => bumpLocalScanCount(prev, code.trim()));
              const onManifest = await isOnCachedManifest(activeStopId, code.trim());
              if (onManifest === false) queuedOffManifest = true;
              showFeedback({
                tone: 'amber',
                title: 'Saved on phone',
                detail: `${code.trim()} – will send when signal returns`,
              });
            } catch (queueErr) {
              setError((queueErr as Error).message ?? 'Could not save scan offline');
              break;
            }
          } else {
            const apiErr = err as { message?: string };
            setError(apiErr.message ?? 'Scan failed');
            break;
          }
        }
      }

      if (succeeded) await loadCounts(activeStopId);
      if (succeeded && !queued && !shouldPause && lastResult) {
        setMessage(scanResultMessage(lastResult, succeeded, code.trim()));
      }
      if (queued) {
        setMessage(
          queuedOffManifest
            ? `${queued} scan${queued > 1 ? 's' : ''} saved on phone — ${code.trim()} is not on this stop's list, dispatch will need to confirm it`
            : `${queued} scan${queued > 1 ? 's' : ''} saved on phone — ${code.trim()} will send when signal returns`,
        );
      }
      void lastEvent;
      setLoading(false);
      inFlightRef.current = false;
    },
    [activeStopId, loadCounts, pauseForResolve, showFeedback],
  );

  useEffect(() => {
    recordScanRef.current = recordScan;
  }, [recordScan]);

  const startCamera = useCallback(
    async (deviceId?: string | null) => {
      if (workComplete) return;
      setError('');
      setCameraStarting(true);
      setDecoderReady(false);
      scanPausedRef.current = false;
      setScanPaused(false);
      stopCamera();
      try {
        setCameraActive(true);
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        if (!videoRef.current) {
          throw new Error('Camera preview element missing');
        }

        const { handle, devices } = await startZxingContinuousScan({
          video: videoRef.current,
          deviceId: deviceId ?? selectedDeviceId,
          onDetected: (code) => {
            if (inFlightRef.current) return;
            void recordScanRef.current(code, 1, 'camera');
          },
          onDuplicateIgnored: (code) => {
            setLastBarcode(code);
            showFeedback(mapScanFeedback({ kind: 'duplicate_ignored', barcode: code }));
          },
        });
        scanHandleRef.current = handle;
        setVideoDevices(devices);
        if (!selectedDeviceId && devices.length) {
          const back = devices.find((d) => /back|rear|environment/i.test(d.label));
          setSelectedDeviceId((back ?? devices[0]).deviceId);
        }
        setDecoderReady(true);
      } catch (err) {
        stopCamera();
        const classified = classifyCameraError(err);
        showFeedback(
          mapScanFeedback({
            kind: 'camera_error',
            cameraKind: classified.kind,
            message: classified.message,
          }),
        );
        const msg = String(err).toLowerCase();
        if (
          classified.kind === 'unknown' &&
          (msg.includes('zxing') || msg.includes('decode') || msg.includes('barcode'))
        ) {
          setError('Barcode recognition failed to start on this device. Use manual entry below.');
        } else {
          setError(classified.message);
        }
      } finally {
        setCameraStarting(false);
      }
    },
    [selectedDeviceId, showFeedback, stopCamera, workComplete],
  );

  // Auto-start camera when guided / stopId query / active servicing stop.
  useEffect(() => {
    if (autoStarted || !activeStopId || !trip || workComplete || cargoMode || cameraActive || cameraStarting) {
      return;
    }
    const servicing = activeStop?.status === 'servicing';
    const shouldAuto = Boolean(queryStopId) || guided || servicing;
    if (!shouldAuto) return;
    setAutoStarted(true);
    void startCamera();
  }, [
    activeStop?.status,
    activeStopId,
    autoStarted,
    cameraActive,
    cameraStarting,
    cargoMode,
    guided,
    queryStopId,
    startCamera,
    trip,
    workComplete,
  ]);

  // Leave camera when work is complete.
  useEffect(() => {
    if (workComplete && cameraActive) {
      stopCamera();
    }
  }, [workComplete, cameraActive, stopCamera]);

  // Surface unresolved overs after load.
  useEffect(() => {
    if (!cameraActive || scanPaused || workComplete) return;
    if (overs.length > 0) {
      pauseForResolve(overs[0], 'over_resolve');
    }
  }, [overs, cameraActive, scanPaused, workComplete, pauseForResolve]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!barcode.trim() || loading || inFlightRef.current) return;
    await recordScan(barcode, Math.max(1, quantity), 'manual_key');
    setBarcode('');
    setQuantity(1);
  };

  const switchCamera = async () => {
    if (videoDevices.length < 2 || cameraStarting || loading) return;
    const idx = videoDevices.findIndex((d) => d.deviceId === selectedDeviceId);
    const next = videoDevices[(idx + 1) % videoDevices.length];
    setSelectedDeviceId(next.deviceId);
    await startCamera(next.deviceId);
  };

  const handleCorrectOver = async () => {
    if (!resolveScan || resolveLoading) return;
    const barcode = resolveScan.barcode;
    const scanId = resolveScan.id;
    setResolveLoading(true);
    setError('');
    const action = newCrewActionMeta();
    const correction = { reason: 'Extra scan was accidental', device: navigator.userAgent };
    try {
      await scanningApi.correctOver(scanId, correction, action);
      await loadCounts(activeStopId);
      resumeScanning();
      showFeedback(
        mapScanFeedback({ kind: 'scan_result', result: 'corrected', barcode }),
      );
    } catch (err) {
      if (shouldQueueOffline(err)) {
        await queueScanCorrection({ scanId, action: 'correct', ...correction }, action, barcode);
      } else {
        setError((err as { message?: string }).message ?? 'Could not correct over-scan');
      }
    } finally {
      setResolveLoading(false);
    }
  };

  const handleAcknowledgeOver = async () => {
    if (!resolveScan || resolveLoading) return;
    const barcode = resolveScan.barcode;
    const scanId = resolveScan.id;
    setResolveLoading(true);
    setError('');
    const action = newCrewActionMeta();
    try {
      await scanningApi.acknowledgeOver(scanId, { device: navigator.userAgent }, action);
      await loadCounts(activeStopId);
      resumeScanning();
      showFeedback({
        tone: 'amber',
        title: 'Extra item confirmed',
        detail: barcode,
      });
    } catch (err) {
      if (shouldQueueOffline(err)) {
        await queueScanCorrection(
          { scanId, action: 'acknowledge', device: navigator.userAgent },
          action,
          barcode,
        );
      } else {
        setError((err as { message?: string }).message ?? 'Could not acknowledge over-scan');
      }
    } finally {
      setResolveLoading(false);
    }
  };

  /** No signal while resolving an over-scan: save the decision and keep scanning. */
  const queueScanCorrection = async (
    payload: Record<string, unknown>,
    action: CrewActionMeta,
    barcode: string,
  ) => {
    try {
      await queueEvent({ type: 'scan_correction', payload, ...action });
      resumeScanning();
      showFeedback({
        tone: 'amber',
        title: 'Saved on phone',
        detail: `${barcode} – will send when signal returns`,
      });
    } catch (queueErr) {
      setError((queueErr as Error).message ?? 'Could not save on phone');
    }
  };

  const handlePhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Compress with the evidence preset so phone photos stay under the API
    // body limit without washing out labels / seal numbers / handwriting.
    setExceptionPhotoLoading(true);
    setExceptionMessage('');
    try {
      setExceptionPhoto(await compressImage(file, EVIDENCE_COMPRESS_OPTIONS));
    } catch {
      setExceptionPhoto('');
      setExceptionMessage('Could not read photo — try another image.');
    } finally {
      setExceptionPhotoLoading(false);
    }
  };

  const handleFileException = async (e: FormEvent) => {
    e.preventDefault();
    if (!trip || !activeStopId || !exceptionReason.trim() || exceptionLoading || exceptionPhotoLoading) return;
    setExceptionLoading(true);
    setExceptionMessage('');
    const action = newCrewActionMeta();
    const exceptionPayload = {
      tripId: trip.id,
      stopId: activeStopId,
      exceptionType,
      reasonCode: exceptionReason.trim(),
      photoKeys: exceptionPhoto ? [exceptionPhoto] : undefined,
    };
    try {
      await exceptionsApi.file(exceptionPayload, action);
      setExceptionMessage('Exception filed — this stop is now unblocked for closing.');
      setExceptionReason('');
      setExceptionPhoto('');
      await reloadTrip();
      stopCamera();
    } catch (err) {
      if (shouldQueueOffline(err)) {
        try {
          await queueEvent({ type: 'exception', payload: exceptionPayload, ...action });
          setExceptionMessage(
            'Exception saved on phone – it will send when signal returns and then unblock this stop.',
          );
          setExceptionReason('');
          setExceptionPhoto('');
          stopCamera();
        } catch (queueErr) {
          setExceptionMessage((queueErr as Error).message ?? 'Could not save on phone');
        }
      } else {
        setExceptionMessage((err as { message?: string }).message ?? 'Failed to file exception');
      }
    } finally {
      setExceptionLoading(false);
    }
  };

  const summary = summarizeCounts(counts, pendingConfirmCount);

  // When this stop is fully scanned, the next step is always "Mark serviced" then
  // signature. Resolve it through the same selector as the Trip screen so the event
  // submitted is exactly the guided CTA (never re-derived from stop.status).
  // No-manifest stops use the trip with offline-saved actions applied, so a cargo check
  // saved on the phone already counts (the next task becomes Mark serviced).
  const continueTask = (() => {
    const taskTrip = cargoMode ? overlayTrip : trip;
    if (!taskTrip || !activeStop || !(workComplete || cargoMode)) return null;
    const task = resolveCrewNextTask({
      clockedIn: true,
      trip: {
        id: taskTrip.id,
        status: taskTrip.status,
        scanningEnabled: taskTrip.scanningEnabled,
        rtbApprovalState: taskTrip.rtbApprovalState,
        stops: (taskTrip.stops ?? []).map((s) => ({
          id: s.id,
          type: s.type,
          status: s.status,
          hasException: s.hasException,
          podId: s.podId,
          cargoCheck: s.cargoCheck ?? null,
        })),
      },
      scanReconciled: reconciled,
      scanNeedsReconcile: needsAttention,
    });
    if (task.stopId !== activeStop.id || eventTypeFromNextTask(task.kind) !== 'end_service') return null;
    return task;
  })();

  const handleContinue = async () => {
    if (!continueTask || !trip || !continueTask.stopId) {
      navigate('/crew');
      return;
    }
    const stopId = continueTask.stopId;
    const podHref = `/crew/pod?stopId=${encodeURIComponent(stopId)}&guided=1`;
    setContinueLoading(true);
    setError('');
    const action = newCrewActionMeta();
    try {
      const pos = await getPosition();
      await tripsApi.advanceStop(
        trip.id,
        stopId,
        'end_service',
        pos ? { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracyMetres: pos.coords.accuracy } : undefined,
        action,
      );
      toast('Scanning done – stop marked serviced');
      navigate(podHref);
    } catch (err) {
      if (shouldQueueOffline(err)) {
        try {
          await queueEvent({
            type: 'stop_advance',
            payload: { tripId: trip.id, stopId, eventType: 'end_service' },
            ...action,
          });
          toast('Saved on phone – will send when signal returns', 'info');
          navigate(podHref);
        } catch (queueErr) {
          setError((queueErr as Error).message ?? 'Could not save on phone');
        }
      } else {
        setError((err as { message?: string }).message ?? 'Could not mark the stop serviced. Go back to the trip and try again.');
      }
    } finally {
      setContinueLoading(false);
    }
  };

  return (
    <CrewLayout>
      <OfflineIndicator />
      <div className={`crew-content crew-scan-page ${cameraMode ? 'crew-scan-camera-mode' : ''}`}>
        {!cameraMode && <Nav items={crewNavItems} variant="crew" />}

        {cameraMode ? (
          <div className="crew-scan-camera">
            <div className="crew-scan-hud">
              <div className="crew-scan-hud-stop">
                <span className="crew-scan-hud-type">{activeStop ? stopTypeLabel(activeStop.type) : 'Stop'}</span>
                {activeStop?.address && (
                  <span className="crew-scan-hud-addr">{activeStop.address}</span>
                )}
              </div>
              <div className="crew-scan-hud-progress" aria-live="polite">
                {progress.expected > 0
                  ? `${progress.scanned} of ${progress.expected}`
                  : `${progress.scanned} scanned`}
              </div>
              <div className="crew-scan-hud-meta">
                <span className={`crew-scan-online ${online ? 'on' : 'off'}`}>
                  {online
                    ? offlinePending > 0 || syncing
                      ? `Sending ${offlinePending}…`
                      : 'Online'
                    : `No signal · ${offlinePending} saved`}
                </span>
                {lastBarcode && (
                  <span className="crew-scan-last-barcode" title={lastBarcode}>
                    Last: {lastBarcode}
                  </span>
                )}
              </div>
            </div>

            <div className="crew-scan-video-wrap">
              <video ref={videoRef} autoPlay playsInline muted className="crew-scan-video" />
              {!decoderReady && (
                <p className="crew-scan-camera-hint">
                  {cameraStarting ? 'Starting camera…' : 'Preparing scanner…'}
                </p>
              )}
            </div>

            <div className="crew-scan-camera-actions">
              {videoDevices.length > 1 && (
                <Button
                  variant="crew"
                  onClick={() => void switchCamera()}
                  disabled={cameraStarting || loading || resolveLoading}
                >
                  Switch camera
                </Button>
              )}
              <Button
                variant="crew"
                onClick={() => {
                  stopCamera();
                  setManualOpen(true);
                }}
                disabled={resolveLoading}
              >
                Pause / Exit
              </Button>
            </div>

            <details
              className="crew-scan-manual"
              open={manualOpen}
              onToggle={(e) => setManualOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary>Manual entry</summary>
              <form onSubmit={handleSubmit} className="scan-form">
                <Input
                  label="Barcode value"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  placeholder="Enter barcode or QR value"
                  autoComplete="off"
                  disabled={!activeStopId || loading}
                />
                <Input
                  label="Quantity"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                  disabled={!activeStopId || loading}
                />
                <Button
                  type="submit"
                  variant="crew"
                  size="crew"
                  fullWidth
                  loading={loading}
                  disabled={!activeStopId || loading || scanPaused}
                >
                  Submit scan
                </Button>
              </form>
            </details>
          </div>
        ) : (
          <div className="scan-page">
            <h2 className="crew-title">{cargoMode ? 'Cargo check' : workComplete ? 'Scan summary' : 'Barcode scan'}</h2>

            {stops.length > 0 ? (
              <div className="scan-stop-select">
                <label className="crew-sr" htmlFor="scan-stop">Stop</label>
                <select
                  id="scan-stop"
                  className="crew-select"
                  value={activeStopId}
                  onChange={(e) => {
                    setSelectedStopId(e.target.value);
                    setAutoStarted(false);
                    setCargoChanging(false);
                    cargo.clear();
                  }}
                >
                  {stops.map((s) => (
                    <option key={s.id} value={s.id}>
                      Stop #{s.sequence} · {stopTypeLabel(s.type)} · {s.address}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="crew-muted">No active stop to scan against.</p>
            )}

            {cargoMode && cargoCheck ? (
              <>
                {(cargo.error || cargo.message) && (
                  <CrewMessage kind={cargo.error ? 'error' : 'info'}>{cargo.error || cargo.message}</CrewMessage>
                )}
                {needsAttention && (
                  <CrewMessage kind="warn">
                    Resolve overages, shortages, or pending scans before marking serviced.
                  </CrewMessage>
                )}
                {cargoStopStatus === 'pending' ? (
                  <>
                    <div className="crew-card">
                      <p className="cargo-scan-hint">{CARGO_SCAN_COPY.noManifest}</p>
                      <p className="crew-muted cargo-scan-hint-sub">{CARGO_SCAN_COPY.arriveFirst}</p>
                    </div>
                    <CrewActionBar>
                      <Button variant="crew" size="crew" fullWidth onClick={() => navigate('/crew')}>
                        Back to trip
                      </Button>
                    </CrewActionBar>
                  </>
                ) : cargoCheck.completed && !cargoChanging ? (
                  <>
                    <div className="crew-card cargo-done-card">
                      <span className="crew-kicker">{CARGO_COPY.title}</span>
                      <p className="cargo-done-summary">
                        <CrewIcon name="check" className="cargo-done-icon" />
                        <span>{cargoSummaryText(cargoCheck)}</span>
                      </p>
                      {cargoCheck.mismatch && (
                        <p className="crew-muted cargo-done-sub">
                          Counted {cargoCheck.countedPieces} of {cargoCheck.expectedPieces} — dispatch has been told.
                        </p>
                      )}
                      <Button type="button" variant="secondary" fullWidth onClick={() => setCargoChanging(true)}>
                        Change
                      </Button>
                    </div>
                    <CrewActionBar>
                      <Button
                        variant="crew"
                        size="crew"
                        fullWidth
                        loading={continueLoading}
                        disabled={continueLoading}
                        onClick={() => void handleContinue()}
                      >
                        {continueTask ? 'Done – mark serviced & get signature' : 'Back to trip'}
                      </Button>
                    </CrewActionBar>
                  </>
                ) : hasException && !cargoChanging ? (
                  <>
                    <CrewMessage kind="warn">Exception filed — stop unblocked.</CrewMessage>
                    <CrewActionBar>
                      <Button
                        variant="crew"
                        size="crew"
                        fullWidth
                        loading={continueLoading}
                        disabled={continueLoading}
                        onClick={() => void handleContinue()}
                      >
                        {continueTask ? 'Done – mark serviced & get signature' : 'Back to trip'}
                      </Button>
                    </CrewActionBar>
                  </>
                ) : (
                  <CrewCargoCheckPanel
                    key={`${activeStopId}:${cargoCheck.mode}`}
                    stopType={cargoStop?.type ?? ''}
                    cargoCheck={cargoCheck}
                    defaultPicName={cargoCheck.picName || cargoStop?.contactName}
                    busy={cargo.busy}
                    intro={CARGO_SCAN_COPY.noManifest}
                    onSubmit={(body) => cargo.submit(activeStopId, body)}
                  />
                )}
                {cargoChanging && cargoCheck.completed && (
                  <button type="button" className="crew-link-btn" onClick={() => setCargoChanging(false)}>
                    Keep what was confirmed
                  </button>
                )}
              </>
            ) : workComplete ? (
              <div className="crew-card crew-scan-summary">
                <dl className="crew-scan-summary-hero">
                  <div>
                    <dt>Expected</dt>
                    <dd className="crew-num">{summary.expected}</dd>
                  </div>
                  <div>
                    <dt>Scanned</dt>
                    <dd className="crew-num">{summary.scanned}</dd>
                  </div>
                </dl>
                <dl className="crew-scan-summary-grid">
                  <div>
                    <dt>Shortages</dt>
                    <dd className="crew-num">{summary.shortages}</dd>
                  </div>
                  <div>
                    <dt>Overages</dt>
                    <dd className="crew-num">{summary.overages}</dd>
                  </div>
                  <div>
                    <dt>Wrong stop</dt>
                    <dd className="crew-num">{summary.wrong}</dd>
                  </div>
                  <div>
                    <dt>Not on list</dt>
                    <dd className="crew-num">{summary.unmanifested}</dd>
                  </div>
                  <div>
                    <dt>Pending</dt>
                    <dd className="crew-num">{summary.pending}</dd>
                  </div>
                </dl>
                {hasException && (
                  <p className="crew-scan-exception-note">Exception filed — stop unblocked.</p>
                )}
                <Button variant="secondary" fullWidth onClick={() => setShowDetail((v) => !v)}>
                  {showDetail ? 'Hide scan detail' : 'Review scans'}
                </Button>
              </div>
            ) : (
              <div className="crew-card crew-scan-progress" aria-live="polite">
                <span className="crew-label">Scanned</span>
                <span className="crew-scan-progress-num crew-num">
                  {progress.expected > 0 ? `${progress.scanned} of ${progress.expected}` : `${progress.scanned} scanned`}
                </span>
              </div>
            )}

            {!cargoMode && (message || error) && (
              <CrewMessage kind={error ? 'error' : 'info'}>{error || message}</CrewMessage>
            )}

            {!cargoMode && !workComplete && needsAttention && (
              <CrewMessage kind="warn">
                Resolve overages, shortages, or pending scans before marking serviced.
              </CrewMessage>
            )}

            {!cargoMode && (!workComplete || showDetail) && (
              <>
                {!workComplete && (
                  <details
                    className="crew-scan-manual"
                    open={manualOpen}
                    onToggle={(e) => setManualOpen((e.target as HTMLDetailsElement).open)}
                  >
                    <summary>Manual entry</summary>
                    <form onSubmit={handleSubmit} className="scan-form">
                      <Input
                        label="Barcode value"
                        value={barcode}
                        onChange={(e) => setBarcode(e.target.value)}
                        placeholder="Enter barcode or QR value"
                        autoComplete="off"
                        disabled={!activeStopId || loading}
                      />
                      <Input
                        label="Quantity"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        value={quantity}
                        onChange={(e) =>
                          setQuantity(Math.max(1, Number(e.target.value) || 1))
                        }
                        disabled={!activeStopId || loading}
                      />
                      <Button
                        type="submit"
                        variant="crew"
                        size="crew"
                        fullWidth
                        loading={loading}
                        disabled={!activeStopId || loading}
                      >
                        Submit scan
                      </Button>
                    </form>
                  </details>
                )}

                {counts.length > 0 && (
                  <div className="manifest-panel">
                    <span className="crew-section-label">
                      Scan check{activeStop ? ` — ${stopTypeLabel(activeStop.type)}` : ''}
                    </span>
                    <div className="manifest-cards">
                      {counts.map((c) => {
                        const label = countStatusLabel(c);
                        return (
                          <div
                            key={c.barcode}
                            className={`crew-card manifest-card ${
                              c.variance !== 0 || c.status === 'not_on_manifest' ? 'variance' : ''
                            }`}
                          >
                            <div className="manifest-card-top">
                              <div className="manifest-card-barcode crew-num">{c.barcode}</div>
                              <span className={`crew-chip ${label === 'OK' ? 'crew-chip-ok' : label === 'Short' ? 'crew-chip-amber' : 'crew-chip-urgent'}`}>
                                {label}
                              </span>
                            </div>
                            <div className="manifest-card-row crew-num">
                              <span>Expected {c.expected}</span>
                              <span>Scanned {c.scanned}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {(cargoMode ? cargoStopStatus !== 'pending' : !workComplete || showDetail) && (
              <div className="exception-section">
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => setShowException((v) => !v)}
                >
                  {showException
                    ? 'Hide exception form'
                    : cargoMode
                      ? 'Problem with the cargo? File an exception'
                      : 'Scan count doesn’t match? File an exception'}
                </Button>

                {showException && (
                  <form onSubmit={handleFileException} className="crew-card exception-form">
                    <div className="form-group">
                      <label className="crew-field-label" htmlFor="exception-type">Exception type</label>
                      <select
                        id="exception-type"
                        className="crew-select"
                        value={exceptionType}
                        onChange={(e) => setExceptionType(e.target.value)}
                      >
                        {EXCEPTION_TYPES.map((t: string) => (
                          <option key={t} value={t}>
                            {humanize(t)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="exception-chips" role="group" aria-label="Common reasons">
                      {EXCEPTION_QUICK_PICKS.map((pick) => {
                        const type = pick.type(activeStop?.type);
                        return (
                          <button
                            key={pick.label}
                            type="button"
                            className={`exception-chip ${exceptionReason === pick.label ? 'active' : ''}`}
                            aria-pressed={exceptionReason === pick.label}
                            onClick={() => {
                              setExceptionReason(pick.label);
                              if ((EXCEPTION_TYPES as readonly string[]).includes(type)) setExceptionType(type);
                            }}
                          >
                            {pick.label}
                          </button>
                        );
                      })}
                    </div>
                    <Input
                      label="Reason"
                      value={exceptionReason}
                      onChange={(e) => setExceptionReason(e.target.value)}
                      placeholder="Describe what happened"
                      required
                    />
                    <div className="form-group">
                      <span className="crew-field-label">Photo (required)</span>
                      <label className={`crew-photo-btn ${exceptionPhotoLoading || exceptionLoading ? 'is-disabled' : ''}`}>
                        <CrewIcon name="camera" />
                        {exceptionPhoto ? 'Retake photo' : 'Take photo'}
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          aria-label="Photo (required)"
                          onChange={(e) => void handlePhotoChange(e)}
                          disabled={exceptionPhotoLoading || exceptionLoading}
                        />
                      </label>
                      {exceptionPhotoLoading && (
                        <span className="crew-photo-status waiting">Preparing photo…</span>
                      )}
                      {!exceptionPhotoLoading && exceptionPhoto && (
                        <span className="crew-photo-status">Photo attached</span>
                      )}
                    </div>
                    <Button
                      type="submit"
                      variant="crew"
                      size="crew"
                      fullWidth
                      loading={exceptionLoading}
                      disabled={exceptionLoading || exceptionPhotoLoading || !exceptionPhoto}
                    >
                      File exception
                    </Button>
                    {!exceptionPhoto && !exceptionPhotoLoading && (
                      <p className="exception-photo-hint">Take a photo of the problem to file the exception.</p>
                    )}
                    {exceptionMessage && (
                      <CrewMessage>{exceptionMessage}</CrewMessage>
                    )}
                  </form>
                )}
              </div>
            )}

            {!cargoMode && (
              <CrewActionBar>
                {workComplete ? (
                  <Button
                    variant="crew"
                    size="crew"
                    fullWidth
                    loading={continueLoading}
                    disabled={continueLoading}
                    onClick={() => void handleContinue()}
                  >
                    {continueTask ? 'Done – mark serviced & get signature' : 'Back to trip'}
                  </Button>
                ) : (
                  <Button
                    variant="crew"
                    size="crew"
                    fullWidth
                    onClick={() => void startCamera()}
                    disabled={!activeStopId || cameraStarting}
                    loading={cameraStarting}
                  >
                    <CrewIcon name="scan" />
                    Open camera scanner
                  </Button>
                )}
              </CrewActionBar>
            )}
          </div>
        )}

        {feedback && overlayKind && (
          <div
            className={`crew-scan-flash crew-scan-flash-${feedback.tone} ${
              overlayKind !== 'feedback' ? 'crew-scan-flash-blocking' : ''
            }`}
            role="status"
            aria-live="assertive"
          >
            <div className="crew-scan-flash-inner">
              <span className="crew-scan-flash-icon" aria-hidden>
                {feedback.tone === 'green' ? '✓' : feedback.tone === 'amber' ? '!' : '✕'}
              </span>
              <strong>{feedback.title}</strong>
              <span className="crew-scan-flash-detail">{feedback.detail}</span>

              {overlayKind === 'wrong_stop' && (
                <Button
                  variant="crew"
                  onClick={resumeScanning}
                  disabled={resolveLoading}
                >
                  Continue scanning
                </Button>
              )}

              {overlayKind === 'over_resolve' && resolveScan && (
                <div className="crew-scan-resolve-actions">
                  <p className="crew-scan-resolve-prompt">
                    Extra scan for <span className="crew-scan-last-barcode">{resolveScan.barcode}</span>
                  </p>
                  <Button
                    variant="crew"
                    fullWidth
                    loading={resolveLoading}
                    disabled={resolveLoading}
                    onClick={() => void handleCorrectOver()}
                  >
                    Extra scan was accidental
                  </Button>
                  <Button
                    variant="secondary"
                    fullWidth
                    loading={resolveLoading}
                    disabled={resolveLoading}
                    onClick={() => void handleAcknowledgeOver()}
                  >
                    Extra physical item confirmed
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {(message || error) && cameraMode && (
          <div className={`scan-message camera-msg ${error ? 'error' : ''}`}>
            {error || message}
          </div>
        )}
      </div>
    </CrewLayout>
  );
}

function bumpLocalScanCount(counts: ScanCount[], barcode: string): ScanCount[] {
  const existing = counts.find((c) => c.barcode === barcode);
  if (existing) {
    const scanned = existing.scanned + 1;
    const variance = scanned - existing.expected;
    return counts.map((c) =>
      c.barcode === barcode
        ? {
            ...c,
            scanned,
            variance,
            status:
              c.status === 'not_on_manifest'
                ? c.status
                : variance > 0
                  ? 'over'
                  : variance < 0
                    ? 'short'
                    : 'ok',
          }
        : c,
    );
  }
  return [
    ...counts,
    { barcode, expected: 0, scanned: 1, variance: 1, status: 'not_on_manifest' as const },
  ];
}

function scanResultMessage(result: ScanEvent['result'], count: number, code: string): string {
  const n = `${count} scan${count > 1 ? 's' : ''}`;
  switch (result) {
    case 'not_on_manifest':
      return `Not on this stop's list — ${code} (${n}). File an exception below before leaving.`;
    case 'over':
      return `Extra item — ${code} (${n}). All expected pieces were already scanned.`;
    case 'wrong_stop':
      return `Wrong stop — ${code} (${n}).`;
    case 'pending_confirmation':
      return `Waiting for dispatch to confirm — ${code} (${n}).`;
    case 'corrected':
      return `Corrected — ${code}`;
    default:
      return `Recorded ${n} — ${code}`;
  }
}

function countStatusLabel(c: ScanCount): string {
  if (c.status === 'not_on_manifest') return 'Not on list';
  if (c.status === 'wrong_stop') return 'Wrong stop';
  if (c.status === 'over' || c.variance > 0) return 'Over';
  if (c.status === 'short' || c.variance < 0) return 'Short';
  return 'OK';
}

function summarizeCounts(counts: ScanCount[], pending: number) {
  let expected = 0;
  let scanned = 0;
  let shortages = 0;
  let overages = 0;
  let wrong = 0;
  let unmanifested = 0;
  for (const c of counts) {
    if (c.expected > 0) {
      expected += c.expected;
      scanned += c.scanned;
    } else {
      scanned += c.scanned;
    }
    if (c.status === 'not_on_manifest') unmanifested += 1;
    else if (c.status === 'wrong_stop') wrong += 1;
    else if (c.variance > 0 || c.status === 'over') overages += 1;
    else if (c.variance < 0 || c.status === 'short') shortages += 1;
  }
  return { expected, scanned, shortages, overages, wrong, unmanifested, pending };
}
